import {
  useEffect, useId, useRef, useState,
  type ClipboardEvent, type DragEvent, type MouseEvent, type ReactNode,
} from 'react';
import { apiUpload } from '../api/client';
import { useT } from '../i18n/LangProvider';
import { toast } from './toast';
import { Icon, type IconName } from './Icon';
import { htmlToMd, mdToHtml, type WikiPageRef } from './markdown';

/** Obsidian-style context menu: formatting, insertion, and clipboard actions. */
type MenuActions = Record<
  'bold' | 'italic' | 'underline' | 'strike' | 'code' | 'h1' | 'h2' | 'h3' | 'quote'
  | 'bullet' | 'numbered' | 'checklist' | 'divider' | 'link' | 'image' | 'table'
  | 'cut' | 'copy' | 'paste',
  () => void
>;

function CtxItem({ label, icon, onPick, hint }: { label: string; icon?: IconName; onPick: () => void; hint?: string }) {
  return (
    <button type="button" className="rich-ctx__item" onMouseDown={(e) => { e.preventDefault(); onPick(); }}>
      <span className="rich-ctx__ico">{icon ? <Icon name={icon} size={13} /> : null}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {hint && <span className="rich-ctx__hint">{hint}</span>}
    </button>
  );
}

/** A category that expands its children to the side on hover (nested submenu). */
function CtxSub({ label, icon, children }: { label: string; icon: IconName; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [flip, setFlip] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  return (
    <div className="rich-ctx__sub" ref={ref}
      onMouseEnter={() => { const r = ref.current?.getBoundingClientRect(); setFlip(!!r && r.right + 200 > window.innerWidth); setOpen(true); }}
      onMouseLeave={() => setOpen(false)}>
      <button type="button" className="rich-ctx__item" onMouseDown={(e) => e.preventDefault()}>
        <span className="rich-ctx__ico"><Icon name={icon} size={13} /></span>
        <span style={{ flex: 1 }}>{label}</span>
        <Icon name="chevR" size={12} />
      </button>
      {open && <div className={`rich-ctx rich-ctx--nested ${flip ? 'is-left' : ''}`} onMouseDown={(e) => e.stopPropagation()}>{children}</div>}
    </div>
  );
}

function RichContextMenu({ x, y, actions, onClose }: {
  x: number; y: number; actions: MenuActions; onClose: () => void; hasWiki: boolean;
}) {
  const t = useT();
  useEffect(() => {
    const close = () => onClose();
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', esc);
    };
  }, [onClose]);

  return (
    <div className="rich-ctx" style={{ left: x, top: y }} onMouseDown={(e) => e.stopPropagation()} role="menu">
      {/* Clipboard stays flat — the most-used actions, always one click away. */}
      <div className="rich-ctx__group">
        <CtxItem label={t('Taglia')} icon="x" hint="Ctrl+X" onPick={actions.cut} />
        <CtxItem label={t('Copia')} icon="copy" hint="Ctrl+C" onPick={actions.copy} />
        <CtxItem label={t('Incolla')} icon="download" hint="Ctrl+V" onPick={actions.paste} />
      </div>
      <div className="rich-ctx__sep" />
      {/* The rest is grouped into nested submenus so the menu stays short. */}
      <div className="rich-ctx__group">
        <CtxSub label={t('Formato testo')} icon="edit">
          <CtxItem label={t('Grassetto')} onPick={actions.bold} hint="Ctrl+B" />
          <CtxItem label={t('Corsivo')} onPick={actions.italic} hint="Ctrl+I" />
          <CtxItem label={t('Sottolineato')} onPick={actions.underline} hint="Ctrl+U" />
          <CtxItem label={t('Barrato')} onPick={actions.strike} />
          <CtxItem label={t('Codice inline')} icon="code" onPick={actions.code} />
        </CtxSub>
        <CtxSub label={t('Blocco')} icon="list">
          <CtxItem label={t('Titolo 1')} onPick={actions.h1} />
          <CtxItem label={t('Titolo 2')} onPick={actions.h2} />
          <CtxItem label={t('Titolo 3')} onPick={actions.h3} />
          <CtxItem label={t('Citazione')} icon="quote" onPick={actions.quote} />
          <CtxItem label={t('Elenco puntato')} icon="listBullet" onPick={actions.bullet} />
          <CtxItem label={t('Elenco numerato')} icon="listNumber" onPick={actions.numbered} />
          <CtxItem label={t('Checklist')} icon="checklist" onPick={actions.checklist} />
        </CtxSub>
        <CtxSub label={t('Inserisci')} icon="plus">
          <CtxItem label={t('Link…')} icon="link" onPick={actions.link} />
          <CtxItem label={t('Immagine…')} icon="image" onPick={actions.image} />
          <CtxItem label={t('Tabella…')} icon="table" onPick={actions.table} />
          <CtxItem label={t('Separatore')} icon="hr" onPick={actions.divider} />
        </CtxSub>
      </div>
    </div>
  );
}

/**
 * Rich text editor modelled on the prototype's editor: a block-type selector,
 * inline tools, and proper dialogs (no browser prompts) for links, images,
 * video and tables. Emits HTML; media is uploaded to the API.
 */

// La sanificazione e i tipi condivisi vivono nel modulo markdown; qui si
// ri-esportano perché diversi schermi li importano già da questo file.
export { isHtml, sanitizeHtml } from './markdown';
export type { WikiPageRef } from './markdown';

function videoEmbed(url: string): string {
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  if (yt) return `<iframe class="md-embed" src="https://www.youtube.com/embed/${yt[1]}" allowfullscreen frameborder="0"></iframe>`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `<iframe class="md-embed" src="https://player.vimeo.com/video/${vimeo[1]}" allowfullscreen frameborder="0"></iframe>`;
  return `<video class="md-video" src="${url}" controls></video>`;
}

const BLOCKS: { label: string; tag: string }[] = [
  { label: 'Paragrafo', tag: 'p' },
  { label: 'Titolo 1', tag: 'h1' },
  { label: 'Titolo 2', tag: 'h2' },
  { label: 'Titolo 3', tag: 'h3' },
  { label: 'Citazione', tag: 'blockquote' },
  { label: 'Codice', tag: 'pre' },
];

function Dialog({ title, onClose, children, width = 460 }: { title: string; onClose: () => void; children: ReactNode; width?: number }) {
  return (
    <div className="modal-backdrop" onMouseDown={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', display: 'grid', placeItems: 'center', zIndex: 200, padding: 20 }}>
      <div className="rich-dialog card" onMouseDown={(e) => e.stopPropagation()} style={{ width: `min(${width}px, 100%)`, padding: 0 }}>
        <div className="spaced" style={{ padding: '12px 16px', borderBottom: '1px solid var(--line,#eee)' }}>
          <strong style={{ fontSize: 14 }}>{title}</strong>
          <button className="btn btn--ghost btn--icon" onClick={onClose}><Icon name="x" size={15} /></button>
        </div>
        <div style={{ padding: 16 }}>{children}</div>
      </div>
    </div>
  );
}

export function RichTextEditor({ value, onChange, minHeight = 300, placeholder, wikiPages, format = 'html' }: {
  value: string;
  onChange: (value: string) => void;
  minHeight?: number;
  placeholder?: string;
  /** When provided, the link dialog offers an internal wiki-page tab. */
  wikiPages?: WikiPageRef[];
  /**
   * `markdown` stores Markdown (the editor stays WYSIWYG and converts on the
   * way in and out) and offers the raw-source view. `html` is the legacy mode.
   */
  format?: 'html' | 'markdown';
}) {
  const tr = useT();
  const ref = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const moreId = useId();
  const [dialog, setDialog] = useState<null | 'link' | 'image' | 'video' | 'table'>(null);
  const [block, setBlock] = useState('Paragrafo');
  const [busy, setBusy] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [source, setSource] = useState(false); // vista sorgente Markdown
  const saved = useRef<Range | null>(null);
  // Keep the custom menu for mouse right-click. Touch devices retain the
  // browser's native long-press selection, copy and paste behaviour.
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);

  const isMarkdown = format === 'markdown';
  const toHtml = (v: string) => (isMarkdown ? mdToHtml(v, wikiPages) : v || '');
  const toValue = (html: string) => (isMarkdown ? htmlToMd(html, wikiPages) : html);

  // Riempie l'area editabile al mount e ogni volta che si torna dal sorgente.
  // Non dipende da `value`: riscrivere l'HTML a ogni battuta sposterebbe il cursore.
  useEffect(() => {
    if (source || !ref.current) return;
    ref.current.innerHTML = toHtml(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  useEffect(() => {
    if (!moreOpen) return;
    const close = (event: PointerEvent) => {
      if (!toolbarRef.current?.contains(event.target as Node)) setMoreOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [moreOpen]);

  const emit = () => onChange(toValue(ref.current?.innerHTML ?? ''));

  /** Remember the caret so dialogs can insert where the user was typing. */
  const remember = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current?.contains(sel.anchorNode)) saved.current = sel.getRangeAt(0).cloneRange();
  };
  const restore = () => {
    ref.current?.focus();
    const sel = window.getSelection();
    if (saved.current && sel) { sel.removeAllRanges(); sel.addRange(saved.current); }
  };

  const exec = (cmd: string, val?: string) => { ref.current?.focus(); document.execCommand(cmd, false, val); emit(); detectBlock(); };
  const insertHTML = (html: string) => { restore(); document.execCommand('insertHTML', false, html); emit(); };

  function detectBlock() {
    const sel = window.getSelection();
    let node = sel?.anchorNode as HTMLElement | null;
    let found = 'Paragrafo';
    while (node && node !== ref.current) {
      const tag = (node.nodeType === 1 ? (node as HTMLElement).tagName : '').toLowerCase();
      const hit = BLOCKS.find((b) => b.tag === tag);
      if (hit) { found = hit.label; break; }
      node = node.parentElement;
    }
    setBlock(found);
  }
  const applyBlock = (label: string) => {
    const tag = BLOCKS.find((b) => b.label === label)?.tag ?? 'p';
    exec('formatBlock', tag.toUpperCase());
  };

  async function upload(file: File | undefined, kind: 'image' | 'video'): Promise<string | null> {
    if (!file) return null;
    setBusy(true);
    try {
      const { url } = await apiUpload(file);
      return url;
    } catch (e) {
      toast.error(`${tr('Caricamento non riuscito')}: ${e instanceof Error ? e.message : tr('errore')}`);
      return null;
    } finally { setBusy(false); }
  }

  function onDrop(e: DragEvent) {
    const f = Array.from(e.dataTransfer.files || []).find((x) => x.type.startsWith('image') || x.type.startsWith('video'));
    if (!f) return;
    e.preventDefault();
    void (async () => {
      const url = await upload(f, f.type.startsWith('video') ? 'video' : 'image');
      if (url) insertHTML(f.type.startsWith('video') ? videoEmbed(url) : `<img src="${url}" alt=""/>`);
    })();
  }
  function onPaste(e: ClipboardEvent) {
    const item = Array.from(e.clipboardData.items).find((i) => i.type.startsWith('image'));
    const f = item?.getAsFile();
    if (!f) return;
    e.preventDefault();
    void (async () => {
      const url = await upload(f, 'image');
      if (url) insertHTML(`<img src="${url}" alt=""/>`);
    })();
  }

  const Btn = ({ title, onClick, children }: { title: string; onClick: () => void; children: ReactNode }) => (
    <button
      type="button"
      className="rich-btn"
      title={title}
      aria-label={title}
      onMouseDown={(event) => { event.preventDefault(); remember(); }}
      onClick={onClick}
    >
      {children}
    </button>
  );

  // ── context menu (right-click) ──
  function openMenu(clientX: number, clientY: number) {
    remember();
    // Keep the menu inside the viewport.
    const x = Math.min(clientX, window.innerWidth - 230);
    const y = Math.min(clientY, window.innerHeight - 380);
    setMenu({ x, y });
  }
  function onContextMenu(e: MouseEvent) {
    e.preventDefault();
    openMenu(e.clientX, e.clientY);
  }
  async function clipboard(action: 'cut' | 'copy' | 'paste') {
    restore();
    try {
      if (action === 'paste') {
        // execCommand('paste') is blocked in most browsers — read the clipboard.
        const text = await navigator.clipboard.readText();
        if (text) document.execCommand('insertText', false, text);
      } else {
        document.execCommand(action);
      }
    } catch {
      // Fallback to the (possibly no-op) execCommand for cut/copy.
      if (action !== 'paste') document.execCommand(action);
    }
    emit();
  }

  const menuAction = (fn: () => void) => () => { fn(); setMenu(null); };

  return (
    <div className="rich-editor">
      <div ref={toolbarRef} className={`rich-toolbar ${source ? 'is-source' : ''}`} role="toolbar" aria-label={tr('Formattazione testo')}>
        <div className="rich-toolbar__primary">
          <select className="rich-block-select" value={block} onChange={(e) => applyBlock(e.target.value)} title={tr('Tipo di blocco')} aria-label={tr('Tipo di blocco')}>
            {BLOCKS.map((b) => <option key={b.tag} value={b.label}>{tr(b.label)}</option>)}
          </select>
          <Btn title={tr('Grassetto (Ctrl+B)')} onClick={() => exec('bold')}><b>B</b></Btn>
          <Btn title={tr('Elenco puntato')} onClick={() => exec('insertUnorderedList')}><Icon name="listBullet" size={14} /></Btn>
          <Btn title={tr('Link')} onClick={() => setDialog('link')}><Icon name="link" size={14} /></Btn>
        </div>

        <button
          type="button"
          className={`rich-btn rich-more-toggle ${moreOpen ? 'is-active' : ''}`}
          title={tr('Altri strumenti')}
          aria-label={tr('Altri strumenti')}
          aria-expanded={moreOpen}
          aria-controls={moreId}
          disabled={source}
          onMouseDown={(event) => { event.preventDefault(); remember(); }}
          onClick={() => setMoreOpen((open) => !open)}
        >
          <Icon name="menu" size={15} />
        </button>

        <div id={moreId} className={`rich-toolbar__secondary ${moreOpen ? 'is-open' : ''}`}>
          <Btn title={tr('Corsivo (Ctrl+I)')} onClick={() => { exec('italic'); setMoreOpen(false); }}><i>I</i></Btn>
          <Btn title={tr('Sottolineato (Ctrl+U)')} onClick={() => { exec('underline'); setMoreOpen(false); }}><u>U</u></Btn>
          <Btn title={tr('Barrato')} onClick={() => { exec('strikeThrough'); setMoreOpen(false); }}><s>S</s></Btn>
          <Btn title={tr('Codice inline')} onClick={() => { const sel = window.getSelection()?.toString(); if (sel) insertHTML(`<code>${sel}</code>`); setMoreOpen(false); }}><Icon name="code" size={14} /></Btn>
          <Btn title={tr('Elenco numerato')} onClick={() => { exec('insertOrderedList'); setMoreOpen(false); }}><Icon name="listNumber" size={14} /></Btn>
          <Btn title={tr('Checklist')} onClick={() => { insertHTML(`<ul><li class="task"><span class="task-box"></span> ${tr('Da fare')}</li></ul>`); setMoreOpen(false); }}><Icon name="checklist" size={14} /></Btn>
          <Btn title={tr('Citazione')} onClick={() => { exec('formatBlock', 'BLOCKQUOTE'); setMoreOpen(false); }}><Icon name="quote" size={14} /></Btn>
          <Btn title={tr('Separatore')} onClick={() => { insertHTML('<hr/>'); setMoreOpen(false); }}><Icon name="hr" size={14} /></Btn>
          <Btn title={tr('Immagine')} onClick={() => { setDialog('image'); setMoreOpen(false); }}><Icon name="image" size={14} /></Btn>
          <Btn title={tr('Video')} onClick={() => { setDialog('video'); setMoreOpen(false); }}><Icon name="video" size={14} /></Btn>
          <Btn title={tr('Tabella')} onClick={() => { setDialog('table'); setMoreOpen(false); }}><Icon name="table" size={14} /></Btn>
          <Btn title={tr('Rimuovi formattazione')} onClick={() => { exec('removeFormat'); setMoreOpen(false); }}><Icon name="x" size={13} /></Btn>
        </div>
        {isMarkdown && (
          <button
            type="button"
            className={`rich-btn rich-source-toggle ${source ? 'is-active' : ''}`}
            title={source ? tr('Torna all’editor visuale') : tr('Mostra il Markdown')}
            aria-label={source ? tr('Torna all’editor visuale') : tr('Mostra il Markdown')}
            aria-pressed={source}
            onMouseDown={(event) => { event.preventDefault(); remember(); }}
            onClick={() => { setMoreOpen(false); setSource((s) => !s); }}
          >
            <Icon name="code" size={14} />
            <span className="rich-source-toggle__label">MD</span>
          </button>
        )}

        {busy && <span className="rich-toolbar__busy" role="status">{tr('Caricamento…')}</span>}
      </div>

      {source ? (
        <textarea
          className="rich-source"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          style={{ minHeight }}
          spellCheck={false}
          aria-label={tr('Sorgente Markdown')}
          placeholder={placeholder}
        />
      ) : (
      <div
        ref={ref}
        contentEditable
        onInput={emit}
        onKeyUp={detectBlock}
        onMouseUp={() => { detectBlock(); remember(); }}
        onBlur={remember}
        onDrop={onDrop}
        onPaste={onPaste}
        onContextMenu={onContextMenu}
        data-placeholder={placeholder}
        className="rich-editable md-body rte-body"
        style={{ minHeight, padding: 16, outline: 'none' }}
        role="textbox"
        aria-multiline="true"
        aria-label={tr('Contenuto')}
        spellCheck
        suppressContentEditableWarning
      />
      )}

      {menu && (
        <RichContextMenu
          x={menu.x} y={menu.y}
          onClose={() => setMenu(null)}
          hasWiki={!!wikiPages}
          actions={{
            bold: menuAction(() => exec('bold')),
            italic: menuAction(() => exec('italic')),
            underline: menuAction(() => exec('underline')),
            strike: menuAction(() => exec('strikeThrough')),
            code: menuAction(() => { const t = window.getSelection()?.toString(); if (t) insertHTML(`<code>${t}</code>`); }),
            h1: menuAction(() => exec('formatBlock', 'H1')),
            h2: menuAction(() => exec('formatBlock', 'H2')),
            h3: menuAction(() => exec('formatBlock', 'H3')),
            quote: menuAction(() => exec('formatBlock', 'BLOCKQUOTE')),
            bullet: menuAction(() => exec('insertUnorderedList')),
            numbered: menuAction(() => exec('insertOrderedList')),
            checklist: menuAction(() => insertHTML('<ul><li class="task"><span class="task-box"></span> Da fare</li></ul>')),
            divider: menuAction(() => insertHTML('<hr/>')),
            link: menuAction(() => setDialog('link')),
            image: menuAction(() => setDialog('image')),
            table: menuAction(() => setDialog('table')),
            cut: menuAction(() => void clipboard('cut')),
            copy: menuAction(() => void clipboard('copy')),
            paste: menuAction(() => void clipboard('paste')),
          }}
        />
      )}

      {dialog === 'link' && (
        <LinkDialog wikiPages={wikiPages} onClose={() => setDialog(null)} onInsert={(html) => { setDialog(null); insertHTML(html); }} />
      )}
      {dialog === 'image' && (
        <ImageDialog onUpload={(f) => upload(f, 'image')} onClose={() => setDialog(null)} onInsert={(html) => { setDialog(null); insertHTML(html); }} />
      )}
      {dialog === 'video' && (
        <VideoDialog onUpload={(f) => upload(f, 'video')} onClose={() => setDialog(null)} onInsert={(html) => { setDialog(null); insertHTML(html); }} />
      )}
      {dialog === 'table' && (
        <TableDialog onClose={() => setDialog(null)} onInsert={(html) => { setDialog(null); insertHTML(html); }} />
      )}
    </div>
  );
}

function LinkDialog({ wikiPages, onClose, onInsert }: { wikiPages?: WikiPageRef[]; onClose: () => void; onInsert: (html: string) => void }) {
  const t = useT();
  const [tab, setTab] = useState<'wiki' | 'url'>(wikiPages?.length ? 'wiki' : 'url');
  const [q, setQ] = useState('');
  const [text, setText] = useState('');
  const [href, setHref] = useState('');
  const pages = (wikiPages ?? []).filter((p) => !q || p.title.toLowerCase().includes(q.toLowerCase()) || p.slug.includes(q.toLowerCase())).slice(0, 20);

  return (
    <Dialog title={t('Inserisci link')} onClose={onClose}>
      {wikiPages?.length ? (
        <div className="rich-tabs" style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          <button className={`rich-tab ${tab === 'wiki' ? 'is-active' : ''}`} onClick={() => setTab('wiki')}>{t('Pagina wiki')}</button>
          <button className={`rich-tab ${tab === 'url' ? 'is-active' : ''}`} onClick={() => setTab('url')}>{t('Link esterno')}</button>
        </div>
      ) : null}

      {tab === 'wiki' ? (
        <>
          <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('Digita per filtrare…')} autoFocus style={{ marginBottom: 8 }} />
          <input className="input" value={text} onChange={(e) => setText(e.target.value)} placeholder={t('Testo personalizzato (opzionale)')} style={{ marginBottom: 10 }} />
          <div className="rich-wiki-list" style={{ maxHeight: 240, overflow: 'auto' }}>
            {pages.length === 0 && <div className="muted" style={{ fontSize: 13, padding: 8 }}>{t('Nessuna pagina.')}</div>}
            {pages.map((p) => (
              <button key={p.slug} className="rich-wiki-row" style={{ display: 'flex', gap: 8, width: '100%', textAlign: 'left', padding: '8px 10px', borderRadius: 6, alignItems: 'center' }}
                      onClick={() => onInsert(`<a href="#" data-wiki-slug="${p.slug}" class="wiki-link">${p.icon ? p.icon + ' ' : ''}${text || p.title}</a>&nbsp;`)}>
                <span style={{ fontSize: 16 }}>{p.icon || '📄'}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600 }}>{p.title}</span>
                  <span className="muted mono" style={{ fontSize: 11 }}>{p.slug}</span>
                </span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <input className="input" value={href} onChange={(e) => setHref(e.target.value)} placeholder="https://example.com" autoFocus style={{ marginBottom: 8 }} />
          <input className="input" value={text} onChange={(e) => setText(e.target.value)} placeholder={t('Etichetta visibile (opzionale)')} style={{ marginBottom: 12 }} />
          <div className="cluster" style={{ justifyContent: 'flex-end' }}>
            <button className="btn" onClick={onClose}>{t('Annulla')}</button>
            <button className="btn btn--primary" disabled={!href} onClick={() => onInsert(`<a href="${href}" target="_blank" rel="noreferrer">${text || href}</a>&nbsp;`)}>{t('Inserisci')}</button>
          </div>
        </>
      )}
    </Dialog>
  );
}

function ImageDialog({ onUpload, onClose, onInsert }: { onUpload: (f?: File) => Promise<string | null>; onClose: () => void; onInsert: (html: string) => void }) {
  const t = useT();
  const [tab, setTab] = useState<'url' | 'file'>('url');
  const [url, setUrl] = useState('');
  const [alt, setAlt] = useState('');
  const [file, setFile] = useState<File | null>(null);

  async function submit() {
    const src = tab === 'url' ? url : await onUpload(file ?? undefined);
    if (!src) return;
    onInsert(`<img src="${src}" alt="${alt}"/>`);
  }

  return (
    <Dialog title={t('Inserisci immagine')} onClose={onClose}>
      <div className="rich-tabs" style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        <button className={`rich-tab ${tab === 'url' ? 'is-active' : ''}`} onClick={() => setTab('url')}>{t('Da URL')}</button>
        <button className={`rich-tab ${tab === 'file' ? 'is-active' : ''}`} onClick={() => setTab('file')}>{t('Carica file')}</button>
      </div>
      {tab === 'url' ? (
        <input className="input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/foto.jpg" autoFocus style={{ marginBottom: 8 }} />
      ) : (
        <div className="rich-uploader" style={{ marginBottom: 8 }}>
          <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          {file && <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>{file.name}</div>}
        </div>
      )}
      <input className="input" value={alt} onChange={(e) => setAlt(e.target.value)} placeholder={t('Descrizione immagine')} style={{ marginBottom: 12 }} />
      <div className="cluster" style={{ justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onClose}>{t('Annulla')}</button>
        <button className="btn btn--primary" disabled={tab === 'url' ? !url : !file} onClick={() => void submit()}>{t('Inserisci')}</button>
      </div>
    </Dialog>
  );
}

function VideoDialog({ onUpload, onClose, onInsert }: { onUpload: (f?: File) => Promise<string | null>; onClose: () => void; onInsert: (html: string) => void }) {
  const t = useT();
  const [tab, setTab] = useState<'url' | 'file'>('url');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);

  async function submit() {
    if (tab === 'url') { if (url) onInsert(videoEmbed(url)); return; }
    const src = await onUpload(file ?? undefined);
    if (src) onInsert(`<video class="md-video" src="${src}" controls></video>`);
  }

  return (
    <Dialog title={t('Inserisci video')} onClose={onClose}>
      <div className="rich-tabs" style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        <button className={`rich-tab ${tab === 'url' ? 'is-active' : ''}`} onClick={() => setTab('url')}>YouTube / Vimeo</button>
        <button className={`rich-tab ${tab === 'file' ? 'is-active' : ''}`} onClick={() => setTab('file')}>{t('Carica file')}</button>
      </div>
      {tab === 'url' ? (
        <input className="input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=…" autoFocus style={{ marginBottom: 12 }} />
      ) : (
        <div className="rich-uploader" style={{ marginBottom: 12 }}>
          <input type="file" accept="video/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          {file && <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>{file.name}</div>}
        </div>
      )}
      <div className="cluster" style={{ justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onClose}>{t('Annulla')}</button>
        <button className="btn btn--primary" disabled={tab === 'url' ? !url : !file} onClick={() => void submit()}>{t('Inserisci')}</button>
      </div>
    </Dialog>
  );
}

function TableDialog({ onClose, onInsert }: { onClose: () => void; onInsert: (html: string) => void }) {
  const t = useT();
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const [header, setHeader] = useState(true);

  const build = () => {
    const cell = '<td>&nbsp;</td>';
    const th = `<th>${t('Colonna')}</th>`;
    const head = header ? `<thead><tr>${th.repeat(cols)}</tr></thead>` : '';
    const bodyRows = Array.from({ length: rows }, () => `<tr>${cell.repeat(cols)}</tr>`).join('');
    onInsert(`<table class="wiki-table">${head}<tbody>${bodyRows}</tbody></table><p><br/></p>`);
  };

  return (
    <Dialog title={t('Inserisci tabella')} onClose={onClose} width={520}>
      <div className="rich-table-toolbar row" style={{ gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <label className="row" style={{ gap: 6, alignItems: 'center', fontSize: 13 }}>
          {t('Righe')} <input className="input" type="number" min={1} max={20} value={rows} onChange={(e) => setRows(Math.max(1, Math.min(20, +e.target.value)))} style={{ width: 70 }} />
        </label>
        <label className="row" style={{ gap: 6, alignItems: 'center', fontSize: 13 }}>
          {t('Colonne')} <input className="input" type="number" min={1} max={10} value={cols} onChange={(e) => setCols(Math.max(1, Math.min(10, +e.target.value)))} style={{ width: 70 }} />
        </label>
        <label className="row" style={{ gap: 6, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={header} onChange={(event) => setHeader(event.target.checked)} /> {t('Intestazione')}
        </label>
      </div>
      <div className="rich-table-prev" style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 2, marginBottom: 12 }}>
        {Array.from({ length: (header ? 1 : 0) + rows }).map((_, r) =>
          Array.from({ length: cols }).map((__, c) => (
            <div key={`${r}-${c}`} style={{ height: 18, borderRadius: 3, background: header && r === 0 ? 'var(--ink-4,#bbb)' : 'var(--bg-sunk,#eee)' }} />
          )),
        )}
      </div>
      <div className="cluster" style={{ justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onClose}>{t('Annulla')}</button>
        <button className="btn btn--primary" onClick={build}>{t('Inserisci tabella')}</button>
      </div>
    </Dialog>
  );
}
